import { useEffect, useId, useRef } from 'react';
import PortfolioPicture from './PortfolioPicture';
import { IconArrowLeft, IconArrowRight, IconClose } from './ui/Icon';
import './lightbox.css';

const SLIDE_DIRECTION = { '-1': 'prev', 0: 'none', 1: 'next' };

const neighbourIndexes = (index, count) => {
    if (count < 2) return [];
    const prev = (index - 1 + count) % count;
    const next = (index + 1) % count;
    return prev === next ? [next] : [prev, next];
};

/**
 * The parts-archive viewer: a native modal <dialog> on the polymer ground.
 * showModal() gives the focus trap, Escape and the inert page for free; focus
 * returns to `returnFocusRef` (the plate that opened it) on every close path.
 */
export default function Lightbox({
    isOpen,
    images,
    index = 0,
    direction = 0,
    captions = [],
    title,
    labels,
    onClose,
    onPrev,
    onNext,
    touchHandlers,
    returnFocusRef
}) {
    const dialogRef = useRef(null);
    const titleId = useId();

    // State drives the element: open -> showModal(), closed -> close().
    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog) return;
        if (isOpen && !dialog.open) dialog.showModal();
        if (!isOpen && dialog.open) dialog.close();
    }, [isOpen]);

    const count = Array.isArray(images) ? images.length : 0;
    if (count === 0) return null;

    // Every close path (Escape, close key, backdrop, state) ends in the native
    // "close" event, so state sync and focus return live in one place.
    const handleNativeClose = () => {
        onClose();
        returnFocusRef?.current?.focus({ preventScroll: true });
    };

    const handleKeyDown = (event) => {
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            onPrev();
        } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            onNext();
        }
    };

    const handleStageClick = (event) => {
        if (event.target === event.currentTarget) onClose();
    };

    const currentIndex = Math.min(index, count - 1);
    const currentFile = images[currentIndex];
    const caption = captions[currentIndex] ?? '';
    const slide = SLIDE_DIRECTION[direction] ?? 'none';

    return (
        <dialog
            ref={dialogRef}
            className="lightbox polymer"
            aria-labelledby={titleId}
            onClose={handleNativeClose}
            onKeyDown={handleKeyDown}
        >
            <div className="lightbox__frame">
                <p className="legend lightbox__title" id={titleId}>{title}</p>

                <button
                    type="button"
                    className="lightbox__key lightbox__close"
                    aria-label={labels.close}
                    onClick={onClose}
                >
                    <IconClose />
                </button>

                <div
                    className="lightbox__stage"
                    onClick={handleStageClick}
                    onTouchStart={touchHandlers?.onTouchStart}
                    onTouchEnd={touchHandlers?.onTouchEnd}
                >
                    <figure className="lightbox__figure">
                        <div className="plate plate--marks lightbox__plate">
                            {isOpen && (
                                <div className="lightbox__slide" data-dir={slide} key={currentFile + currentIndex}>
                                    <PortfolioPicture
                                        file={currentFile}
                                        alt={caption}
                                        variant="large"
                                        loading="eager"
                                        fetchPriority="high"
                                    />
                                </div>
                            )}
                        </div>
                        <figcaption className="lightbox__caption" aria-live="polite">
                            <span className="legend lightbox__name">{caption}</span>
                            <span className="legend lightbox__count">
                                {currentIndex + 1} / {count}
                            </span>
                        </figcaption>
                    </figure>
                </div>

                {count > 1 && (
                    <>
                        <button
                            type="button"
                            className="lightbox__key lightbox__prev"
                            aria-label={labels.prev}
                            onClick={onPrev}
                        >
                            <IconArrowLeft />
                        </button>
                        <button
                            type="button"
                            className="lightbox__key lightbox__next"
                            aria-label={labels.next}
                            onClick={onNext}
                        >
                            <IconArrowRight />
                        </button>
                    </>
                )}

                {/* Neighbours load while the current plate is viewed, so a step never waits. */}
                {isOpen && (
                    <div className="lightbox__preload" hidden>
                        {neighbourIndexes(currentIndex, count).map((i) => (
                            <PortfolioPicture
                                key={images[i] + i}
                                file={images[i]}
                                alt=""
                                variant="large"
                                loading="eager"
                            />
                        ))}
                    </div>
                )}
            </div>
        </dialog>
    );
}
